import React from 'react'
import { Typography } from '@mui/material'

/** "Also known as: ..." hint shown wherever a region/region-set option has
 * aliases -- renders nothing when there are none. */
export function AliasHint({ names }: { names?: string[] | null }) {
  if (!names || names.length === 0) return null
  return (
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', fontSize: '0.85em', fontStyle: 'italic' }}
    >
      Also known as: {names.join(', ')}
    </Typography>
  )
}

export function renderTextWithLinks(text: string | null | undefined) {
  if (!text) {
    return text
  }
  // match URLs
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g
  const parts = text.split(urlRegex)

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      // remove trailing punctuation
      const trailingPunctuationMatch = part.match(/([.,;:!?]+)$/)
      const trailingPunctuation = trailingPunctuationMatch
        ? trailingPunctuationMatch[0]
        : ''
      const cleanUrl = trailingPunctuation
        ? part.slice(0, -trailingPunctuation.length)
        : part

      // add https if missing
      const href = cleanUrl.startsWith('www.')
        ? `https://${cleanUrl}`
        : cleanUrl

      return (
        <React.Fragment key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {cleanUrl}
          </a>
          {trailingPunctuation}
        </React.Fragment>
      )
    }
    return part
  })
}

export function getAsciiName(name: string): string {
  return name
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

const GROUP_PREVIEW_COUNT = 5

export function formatGroupLocationsPreview(
  regionIds: number[],
  regionNameById?: Map<number, string>,
): string {
  const base = `Adds ${regionIds.length} locations`
  if (!regionNameById) return base

  const previewNames = regionIds
    .slice(0, GROUP_PREVIEW_COUNT)
    .map((id) => regionNameById.get(id))
    .filter((name): name is string => Boolean(name))
  if (previewNames.length === 0) return base

  const remaining = regionIds.length - previewNames.length
  return remaining > 0
    ? `${base}: ${previewNames.join(', ')} +${remaining} more`
    : `${base}: ${previewNames.join(', ')}`
}

/**
 * Fuzzy match for autocomplete filtering with word-boundary prefix matching.
 * Strips macrons, special characters, and does case-insensitive matching.
 *
 * @param option - The option string to check
 * @param search - The search term to match
 * @returns true if any word in option starts with search (both normalized)
 *
 * @example
 * fuzzyMatch("Iwi/Māori", "iw") // true (word "iwi" starts with "iw")
 * fuzzyMatch("Big Dogs", "dogs") // true (word "dogs" starts with "dogs")
 * fuzzyMatch("Kiwi", "iw") // false (word "kiwi" doesn't start with "iw")
 * fuzzyMatch("Māori", "mao") // true (normalized "maori" starts with "mao")
 */
export function fuzzyMatch(option: string, search: string): boolean {
  if (!search) return true

  // Clean: lowercase, strip macrons, keep only alphanumeric and spaces
  const clean = (s: string) =>
    getAsciiName(s).replace(/[^a-z0-9\s]/g, '').trim()

  const cleanOption = clean(option)
  const cleanSearch = clean(search)

  // Check if ANY word in option starts with search
  const words = cleanOption.split(/\s+/)
  return words.some(word => word.startsWith(cleanSearch))
}
